import React from "react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { formatDayMonthYear } from "@/lib/dates";
import { purgeDate } from "@/lib/deletionGrace";
import { useAccountSettingsStore } from "stores/useAccountSettingsStore";
import { OrgBanner } from "./OrgBanner";

/// A bell badge is missable and a member who never opens it loses their work
/// with no notice at all. This sits above every surface of a scheduled org
/// until the window closes or someone cancels. No dismiss. A doomed SPACE
/// needs no banner: it is in the Trash, and the rail no longer shows it.
///
/// Its button leads to the cancel, never performs it: a cancel affects the
/// whole org, so it is made where the rest of that decision lives.
export const OrgDeletionBanner: React.FC = () => {
  const navigate = useNavigate();
  const org = useOrgContextStore(
    useShallow((s) => s.orgs.find((o) => o.org_id === s.activeOrgId)),
  );
  if (!org?.deletion_scheduled_at) return null;

  const closes = org.purge_at
    ? formatDayMonthYear(org.purge_at)
    : purgeDate(org.deletion_scheduled_at);

  if (org.deletion_started_at) {
    return (
      <OrgBanner>
        <span className="font-medium">{org.name}</span> is being deleted. This
        can no longer be stopped.
      </OrgBanner>
    );
  }

  // D55: the personal org goes with the account, on the account's clock — and
  // the account settings own the cancel.
  if (org.is_personal) {
    return (
      <OrgBanner
        action={{
          label: "Cancel deletion",
          onClick: () => useAccountSettingsStore.getState().show("home"),
        }}
      >
        Your account is scheduled for deletion on {closes}.
      </OrgBanner>
    );
  }

  const isGovernor = org.role === "owner" || org.role === "org_admin";
  return (
    <OrgBanner
      action={
        isGovernor
          ? {
              label: "Cancel deletion",
              onClick: () => navigate(`/organizations/${org.org_id}`),
            }
          : undefined
      }
    >
      <span className="font-medium">{org.name}</span> is scheduled for deletion
      on {closes}.{!isGovernor && " An owner or admin can cancel it."}
    </OrgBanner>
  );
};
